import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { isCoarsePointer, pointHitLayer, pointHitRadius } from '../utils/point-hit-area';
import { dateLong, esc } from '../utils/formatters';

// Schools, preschools, nurseries and children's clubs from Metroskop's import
// of the two registers (EducationFacilityMapService, /api/placowki-edukacyjne).

export const EDUCATION_GROUPS = ['nursery', 'preschool', 'primary', 'secondary', 'other'];

// One colour per group. MapLibre paint cannot read CSS variables, so these
// mirror the --nm-layer-education-* tokens of the map theme
// (scss/newmobilemap/base/_theme.scss); a JavaScript test keeps them equal.
export const EDUCATION_GROUP_COLORS = {
    nursery: '#db2777',
    preschool: '#d97706',
    primary: '#059669',
    secondary: '#4f46e5',
    other: '#57534e',
};

// Legend names of the groups; EducationFacilityMapService::labels() sends the
// same names for the popup (a PHP contract test compares them).
export const EDUCATION_GROUP_LABELS = {
    nursery: 'Żłobek lub klub dziecięcy',
    preschool: 'Przedszkole lub punkt przedszkolny',
    primary: 'Szkoła podstawowa',
    secondary: 'Szkoła ponadpodstawowa',
    other: 'Inna placówka oświatowa',
};

// Registry states, each with its own point: an active facility is a full point
// with a white ring, a planned one a ring with an empty centre, an unconfirmed
// one a ring with a light fill, a suspended one a full point with a dark ring.
export const EDUCATION_STATUSES = ['active', 'planned', 'unconfirmed', 'suspended'];
const ACTIVE_STATUS = 'active';
// Mirror --nm-layer-education-ring and --nm-layer-education-suspended. White
// separates neighbours on the light basemap, which stays light in both themes.
export const EDUCATION_RING_COLOR = '#ffffff';
export const EDUCATION_SUSPENDED_RING_COLOR = '#292524';
// Fill of an unconfirmed point; the legend and the card swatch use the same share.
export const EDUCATION_UNCONFIRMED_FILL_OPACITY = 0.35;
// Tone of the shared .ms-status badge of each state that is not active.
export const EDUCATION_STATUS_TONES = { planned: 'info', unconfirmed: 'warning', suspended: 'neutral' };
const POINT_RADIUS_FAR = 4.5;
const POINT_RADIUS_NEAR = 7;
const POINT_RADIUS_NEAR_ZOOM = 17;
const POINT_STROKE_WIDTH = 2;
const STATUS_STROKE_WIDTH = 2.5;

function groupColorExpression() {
    const expression = ['match', ['get', 'group']];
    EDUCATION_GROUPS.forEach((group) => expression.push(group, EDUCATION_GROUP_COLORS[group]));
    expression.push(EDUCATION_GROUP_COLORS.other);
    return expression;
}

function knownStatus(value) {
    return EDUCATION_STATUSES.indexOf(value) !== -1 ? value : ACTIVE_STATUS;
}

function sourceLine(properties, metadata) {
    const labels = (metadata && metadata.labels && metadata.labels.sources) || {};
    const source = labels[properties.source] || 'Rejestr Szkół i Placówek Oświatowych';
    const checked = metadata && metadata.checkedAt ? dateLong(metadata.checkedAt) : '';
    return 'Źródło: ' + source + (checked ? ', stan rejestru sprawdzony ' + checked : '') + '.';
}

function statusLabel(properties, metadata) {
    if (!properties.status || properties.status === ACTIVE_STATUS) return '';
    const labels = (metadata && metadata.labels && metadata.labels.statuses) || {};
    return labels[properties.status] || '';
}

/** The shared status badge of a state that is not active; an active facility has none. */
export function educationStatusBadgeHtml(properties, metadata) {
    const status = properties && properties.status;
    const tone = EDUCATION_STATUS_TONES[status];
    const labels = (metadata && metadata.labels && metadata.labels.statusBadges) || {};
    if (!tone || !labels[status]) return '';
    return '<span class="ms-status ms-status--' + tone + '" data-education-status="' + esc(status) + '">' + esc(labels[status]) + '</span>';
}

/** The facility in the details panel: name, kind, address, status, placement and source. */
export function educationFacilityHtml(properties, metadata) {
    const p = properties || {};
    const labels = (metadata && metadata.labels) || {};
    const group = (labels.groups && labels.groups[p.group]) || EDUCATION_GROUP_LABELS[p.group] || EDUCATION_GROUP_LABELS.other;
    const row = (label, value) => '<div class="nm-detail-row"><span>' + esc(label)
        + '</span><span class="nm-detail-value--wrap">' + esc(value) + '</span></div>';
    // A nursery's title is already its kind („Żłobek”).
    const type = [p.type !== p.title ? p.type : '', p.publicStatus].filter(Boolean).join(', ');
    const status = statusLabel(p, metadata);
    const placement = p.placement && p.placement !== 'exact' && labels.placements ? labels.placements[p.placement] : '';
    const anonymousNote = p.anonymous && labels.anonymous ? labels.anonymous.note : '';
    return '<div class="nm-education-head"><span class="nm-section-label">' + esc(group) + '</span>'
        + educationStatusBadgeHtml(p, metadata) + '</div>'
        + '<h3 class="nm-detail-title">' + esc(p.title || group) + '</h3>'
        + (type ? row('Rodzaj placówki', type) : '')
        + (p.address ? row('Adres', p.address) : '')
        + (!p.address && p.locality ? row('Miejscowość', p.locality) : '')
        + (status ? row('Stan w rejestrze', status) : '')
        + (placement ? '<p class="ms-note ms-note--warning nm-education-note">' + esc(placement) + '</p>' : '')
        + (anonymousNote ? '<p class="nm-detail-copy">' + esc(anonymousNote) + '</p>' : '')
        + '<p class="nm-detail-copy">' + esc(sourceLine(p, metadata)) + '</p>';
}

/** Result card of a picked point; selecting it opens educationFacilityHtml(). */
export function educationFacilityCardHtml(hit, metadata) {
    const p = (hit && hit.properties) || {};
    const labels = (metadata && metadata.labels) || {};
    const group = EDUCATION_GROUPS.indexOf(p.group) !== -1 ? p.group : 'other';
    const groupLabel = (labels.groups && labels.groups[group]) || EDUCATION_GROUP_LABELS[group];
    const placeLine = p.address || p.locality || '';
    return '<button class="ms-card ms-card--grid nm-list-card" type="button" data-tap-hit-kind="education" data-tap-hit-id="' + esc(String(hit && hit.id)) + '">'
        + '<div class="nm-list-card-top"><span class="nm-pill"><span class="nm-education-swatch nm-education-swatch--' + group
        + ' is-' + knownStatus(p.status) + '" aria-hidden="true"></span>' + esc(groupLabel) + '</span>'
        + educationStatusBadgeHtml(p, metadata) + '</div>'
        + '<div class="nm-list-address">' + esc(p.title || groupLabel) + '</div>'
        + (placeLine ? '<div class="nm-card-subline">' + esc(placeLine) + '</div>' : '')
        + '</button>';
}

/** Help of the layer panel: source, placement, gaps and use (the panel adds the zoom sentence). */
export function educationLayerInfoHtml() {
    return '<div class="nm-layer-info-title">Szkoły, przedszkola i żłobki</div>'
        + '<div class="nm-layer-info-row"><span>Źródło danych</span><span>Rejestr Szkół i Placówek Oświatowych oraz rejestr żłobków i klubów dziecięcych</span></div>'
        + '<div class="nm-layer-info-row"><span>Aktualizacja</span><span>Metroskop sprawdza rejestry codziennie</span></div>'
        + '<p class="nm-layer-info-copy">Szkoły i przedszkola nie mają w rejestrze współrzędnych, dlatego ich adres dopasowujemy do państwowych punktów adresowych. Żłobki i kluby dziecięce mają współrzędne z rejestru, które porównujemy z adresem.</p>'
        + '<p class="nm-layer-info-copy">Kolor punktu oznacza grupę: żłobki i kluby dziecięce, przedszkola i punkty przedszkolne, szkoły podstawowe, szkoły ponadpodstawowe oraz inne placówki oświatowe, na przykład poradnie, bursy i szkoły policealne. Wygląd punktu pokazuje stan w rejestrze: pełny punkt z białą obwódką to placówka czynna, punkt z pustym środkiem to placówka planowana, jasne wypełnienie oznacza wpis niepotwierdzony w ostatniej aktualizacji rejestru, a ciemna obwódka działalność zawieszoną przez prowadzącego. Karta placówki pokazuje stan także słowami.</p>'
        + '<p class="nm-layer-info-copy">Na mapie brakuje placówek zlikwidowanych oraz tych, których adresu nie udało się jednoznacznie dopasować do punktu adresowego. Przy dopasowaniu po nazwie ulicy lub miejscowości karta placówki pokazuje dopisek „położenie przybliżone”. Przy żłobkach i klubach dziecięcych podajemy rodzaj placówki i miejscowość.</p>'
        + '<p class="nm-layer-info-copy">Warstwa pomaga sprawdzić, jak daleko od domu lub działki są żłobek, przedszkole i szkoła, czyli ocenić codzienne dojazdy rodziny z dziećmi. Rejonizację szkół, wolne miejsca i godziny pracy placówki sprawdzisz w gminie i w samej placówce. Kliknij punkt, aby zobaczyć nazwę, rodzaj i adres.</p>';
}

export default class EducationFacilitiesLayer extends GeoJsonFetchLayer {
    static ID = 'education';
    static DESCRIPTION = 'Szkoły, przedszkola i żłobki';
    // Mirrors EducationFacilityMapService::MIN_ZOOM. The densest view at zoom 13
    // (central Warsaw) holds about 1 200 facilities, at zoom 12 about 2 300.
    static MIN_ZOOM = 13;
    static API_PATH = 'api/placowki-edukacyjne';
    // Margin around the visible map, as for the other point layers.
    static BBOX_PADDING = 0.12;
    static SOURCE_ID = 'src-education';
    static POINT_LAYER_ID = 'lyr-education-points';
    // An invisible, larger circle under the points takes clicks and taps (see point-hit-area.js).
    static HIT_LAYER_ID = 'lyr-education-hit';
    static LAYER_IDS = [EducationFacilitiesLayer.POINT_LAYER_ID, EducationFacilitiesLayer.HIT_LAYER_ID];
    static VISIBLE_DOT_RADIUS = POINT_RADIUS_NEAR + STATUS_STROKE_WIDTH;

    getSourceId() {
        return EducationFacilitiesLayer.SOURCE_ID;
    }

    getLayerIds() {
        return EducationFacilitiesLayer.LAYER_IDS;
    }

    getFeatureKey(feature) {
        return feature && feature.properties ? feature.properties.id : null;
    }

    getMetadata() {
        return this._metadata || {};
    }

    /** The zoom rounded like BaseLayer.canActivate(), so client and server agree at the threshold. */
    requestZoom() {
        const map = this.getMap();
        const zoom = map && map.getZoom ? map.getZoom() : EducationFacilitiesLayer.MIN_ZOOM;
        return Math.round(zoom * 10) / 10;
    }

    buildUrl() {
        const bbox = this.config.getCurrentBbox ? this.config.getCurrentBbox(EducationFacilitiesLayer.BBOX_PADDING) : '';
        return String(this.config.baseUrl || '/') + EducationFacilitiesLayer.API_PATH
            + '?bbox=' + encodeURIComponent(bbox)
            + '&zoom=' + encodeURIComponent(this.requestZoom());
    }

    /** One style per registry state (see EDUCATION_STATUSES); an unknown state is drawn as active. */
    pointPaint() {
        const color = groupColorExpression();
        const status = ['get', 'status'];
        return {
            'circle-radius': ['interpolate', ['linear'], ['zoom'],
                EducationFacilitiesLayer.MIN_ZOOM, POINT_RADIUS_FAR, POINT_RADIUS_NEAR_ZOOM, POINT_RADIUS_NEAR],
            'circle-color': ['match', status, 'planned', EDUCATION_RING_COLOR, color],
            'circle-opacity': ['match', status, 'unconfirmed', EDUCATION_UNCONFIRMED_FILL_OPACITY, 1],
            'circle-stroke-color': ['match', status,
                'planned', color, 'unconfirmed', color, 'suspended', EDUCATION_SUSPENDED_RING_COLOR, EDUCATION_RING_COLOR],
            'circle-stroke-width': ['match', status, ['planned', 'unconfirmed', 'suspended'], STATUS_STROKE_WIDTH, POINT_STROKE_WIDTH],
            'circle-stroke-opacity': 1,
        };
    }

    _unbindInteractions() {
        if (!this._boundMap) return;
        this._boundMap.off('mouseenter', EducationFacilitiesLayer.HIT_LAYER_ID, this._onEnter);
        this._boundMap.off('mouseleave', EducationFacilitiesLayer.HIT_LAYER_ID, this._onLeave);
        this._boundMap = null;
    }

    _bindInteractions(map) {
        if (this._boundMap === map) return;
        this._unbindInteractions();
        // Clicks go through the map's point query (interactive-hit-dispatcher).
        this._onEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
        this._onLeave = () => { map.getCanvas().style.cursor = ''; };
        map.on('mouseenter', EducationFacilitiesLayer.HIT_LAYER_ID, this._onEnter);
        map.on('mouseleave', EducationFacilitiesLayer.HIT_LAYER_ID, this._onLeave);
        this._boundMap = map;
    }

    _addLayers(map, sourceId) {
        map.addLayer({
            id: EducationFacilitiesLayer.POINT_LAYER_ID,
            type: 'circle',
            source: sourceId,
            paint: this.pointPaint(),
        });
        map.addLayer(pointHitLayer(
            EducationFacilitiesLayer.HIT_LAYER_ID,
            sourceId,
            pointHitRadius(isCoarsePointer(typeof window !== 'undefined' ? window : null)),
            EducationFacilitiesLayer.VISIBLE_DOT_RADIUS
        ));
        this._bindInteractions(map);
    }

    renderData(data) {
        if (data && data.metadata) this._metadata = data.metadata;
        return this.renderSourceData(data, this._addLayers.bind(this));
    }

    clear() {
        this._unbindInteractions();
        super.clear();
    }
}
